import { FC, PropsWithChildren } from 'react';

import {
  CssBaseline,
  ThemeProvider as MuiThemeProvider,
  PaletteMode,
  Theme,
  ThemeOptions,
  createTheme,
  responsiveFontSizes,
} from '@mui/material';
import { grey } from '@mui/material/colors';

const getDesignTokens = (mode: PaletteMode): ThemeOptions => ({
  zIndex: {
    drawer: 2,
    modal: 1,
  },
  typography: {
    fontFamily: 'iberia_text-regularWEB',
    fontSize: 16,
  },
  palette: {
    mode,
    primary: grey,
    secondary: {
      main: '#d71f29',
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 768,
      lg: 1124,
      xl: 1500,
    },
  },
  components: {
    MuiSwitch: {
      defaultProps: {
        color: 'secondary',
      },
    },
  },
});

export const THEMES = ['Corporate', 'Minimalist', 'Dark'];

type ThemeAppTuple = typeof THEMES;

export type ThemeApp = ThemeAppTuple[number];

export const isThemeApp = (value: string): value is ThemeApp => THEMES.includes(value as ThemeApp);

const getTheme = (themeApp: ThemeApp, themeOptions: ThemeOptions): Theme => {
  const theme = createTheme(themeOptions);

  const commonPalette = {
    iberiaRed: theme.palette.augmentColor({
      color: {
        main: '#d71f29',
      },
    }),
  };

  const commonTypographyRedWhite = {
    typography: {
      h3: {
        color: '#444444',
        fontSize: '35px',
      },
      h5: {
        color: '#666666',
      },
    },
  };

  const redOverride = {
    palette: {
      ...commonPalette,
      background: {
        default: '#eeeeee',
      },
    },
    ...commonTypographyRedWhite,
  };

  const whiteOverride = {
    palette: {
      ...commonPalette,
      background: {
        paper: '#f5f5f5',
      },
    },
    ...commonTypographyRedWhite,
  };

  const darkOverride = {
    palette: {
      ...commonPalette,
    },
    typography: {
      h3: {
        fontSize: '35px',
      },
    },
  };

  return responsiveFontSizes(
    createTheme(
      theme,
      themeApp === 'Corporate'
        ? redOverride
        : themeApp === 'Minimalist'
          ? whiteOverride
          : darkOverride,
    ),
  );
};

interface Props {
  theme: ThemeApp;
}

const ThemeProvider: FC<PropsWithChildren<Props>> = ({ theme, children }) => (
  <MuiThemeProvider theme={getTheme(theme, getDesignTokens(theme === 'Dark' ? 'dark' : 'light'))}>
    <CssBaseline />
    {children}
  </MuiThemeProvider>
);

export default ThemeProvider;
